// ===========================================
// Admin Enquiries Pipeline Page (enquiries plan §5.7)
// ===========================================
// The active pipeline, one tab per stage, and the archive. Stage names are the school's; days are
// shown dd/mm/yyyy and judged against the school's today (`config.schoolToday`).

import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Tab,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Table,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  ToggleButton,
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  apiErrorMessage,
  useArchiveEnquiry,
  useDeleteEnquiry,
  useEnquiries,
  useEnquiryConfig,
  useEnquiryFlags,
  useEnquiryStats,
  useSetNextAction,
} from '../../hooks/useEnquiries';
import { useAuth } from '../../contexts/useAuth';
import { useSchoolTimezone } from '../../contexts/TenantContext';
import { useOwnerTitle } from '../../hooks/useOwnerTitle';
import { useRoleColorScheme } from '../../hooks/useRoleColorScheme';
import { formatInSchoolTz } from '../../utils/dateUtils';
import { brandColors, getContrastTextColor, statusColors } from '../../styles/brandColors';
import {
  ENQUIRY_STAGES,
  type EnquiryFilters,
  type EnquiryFlagKey,
  type EnquiryListItem,
  type EnquiryStage,
} from '../../api/enquiry.api';
import PageHeader from '../../components/common/PageHeader';
import { usePermissions } from '../../hooks/usePermissions';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import EnquiryDetailDrawer from '../../components/admin/EnquiryDetailDrawer';
import CreateEnquiryDialog from '../../components/admin/CreateEnquiryDialog';
import EnquiryFlagChips from '../../components/admin/enquiries/EnquiryFlagChips';
import { ENQUIRY_FLAG_KEY_LIST, ENQUIRY_FLAG_LABELS, isEnquiryFlagKey } from '../../utils/enquiryFlags';
import SpamCheckChip, { ENQUIRY_SPAM_CHECK_TOOLTIP } from '../../components/admin/SpamCheckChip';
import MigrationReviewDialog from '../../components/admin/enquiries/MigrationReviewDialog';
import {
  STAGE_COLORS,
  TEMPERATURE_COLORS,
  TEMPERATURE_LABELS,
  ballWithLabel,
  carriesBall,
  dueState,
  formatDay,
  interestChips,
  isArchived,
  isEnquiryId,
  ownerLabel,
  stageLabel,
  stageView,
} from '../../components/admin/enquiries/enquiryUi';

type TabValue = 'ACTIVE' | 'ARCHIVED' | EnquiryStage;
type OwnerFilter = 'ALL' | 'me' | 'unassigned';
type SortChoice = 'due' | 'received' | 'activity';

const SORTS: Record<SortChoice, Pick<EnquiryFilters, 'sortBy' | 'sortOrder'>> = {
  due: {},
  received: { sortBy: 'receivedOn', sortOrder: 'desc' },
  activity: { sortBy: 'lastActivityAt', sortOrder: 'desc' },
};

// Stages that archive on entry: their tab reads the archived view (review 5.7 M4).
const ARCHIVING_STAGES: readonly EnquiryStage[] = ['ENROLLED', 'SPAM'];

/** The tab named by `?tab=`: a known stage (or ARCHIVED), otherwise Active. */
const initialTab = (raw: string | null): TabValue =>
  raw && ((ENQUIRY_STAGES as readonly string[]).includes(raw) || raw === 'ARCHIVED') && raw !== 'RESOLVED_TO_REVIEW'
    ? (raw as TabValue)
    : 'ACTIVE';

/** `dd/mm/yyyy` of an instant, in the school's zone. */
const dayIn = (iso: string, timeZone: string) =>
  formatInSchoolTz(iso, timeZone, { day: '2-digit', month: '2-digit', year: 'numeric' });

/** Spam and Enrolled archive on entry and leave only by a stage move; the server refuses Unarchive there (409). */
const mayToggleArchive = (e: EnquiryListItem): boolean =>
  !(isArchived(e.archivedAt) && ARCHIVING_STAGES.includes(e.stage));

export default function EnquiriesPage() {
  const permissions = usePermissions();
  const { user } = useAuth();
  const timeZone = useSchoolTimezone();
  const ownerTitle = useOwnerTitle();
  // The owner chips' dots: the school's User Role Colors for the owner tier and a teacher (#629).
  const roleScheme = useRoleColorScheme();
  const ownerChipColors = { owner: roleScheme.ADMIN.background, teacher: roleScheme.TEACHER.background };
  // The bin is the owner's (roles slice 2: `DELETE /admin/enquiries/:id` is ownerOnly and
  // `enquiries.delete` left the catalogue); staff never see it.
  const mayDeleteEnquiry = permissions.isOwner;
  const mayWrite = permissions.can('enquiries', 'write');

  // The dashboard's "Enquiries - New" card links here with `?tab=NEW`; read once, then local state.
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<TabValue>(() => initialTab(searchParams.get('tab')));
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [owner, setOwner] = useState<OwnerFilter>('ALL');
  const [needsReviewOnly, setNeedsReviewOnly] = useState(false);
  // The flag filter lives IN the URL (`?flag=OVERDUE`: the dashboard links here), so it is read from
  // it on every render and written back to it; an unknown value is ignored.
  const flagParam = searchParams.get('flag');
  const urlFlag: EnquiryFlagKey | undefined = isEnquiryFlagKey(flagParam) ? flagParam : undefined;
  const [sort, setSort] = useState<SortChoice>('due');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  // The Meet & Greet page's "Enquiry: …" chip links here with `?enquiry=<id>`: open that one, once.
  // Only a CUID2-shaped id is taken (it goes into an API path); anything else is ignored. The param is
  // one-shot: removed from the URL below, so a refresh or Back does not reopen it.
  const deepLinkId = isEnquiryId(searchParams.get('enquiry')) ? searchParams.get('enquiry') : null;
  const [selectedEnquiryId, setSelectedEnquiryId] = useState<string | null>(deepLinkId);
  const [drawerOpen, setDrawerOpen] = useState(!!deepLinkId);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [menu, setMenu] = useState<{ anchor: HTMLElement; enquiry: EnquiryListItem } | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  // Consume `?enquiry=` once (valid or not): replace the history entry so Back does not return to it.
  const strippedEnquiryParam = useRef(false);
  useEffect(() => {
    if (strippedEnquiryParam.current || !searchParams.has('enquiry')) return;
    strippedEnquiryParam.current = true;
    const next = new URLSearchParams(searchParams);
    next.delete('enquiry');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce the search box.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const { data: config } = useEnquiryConfig();
  const { data: stats } = useEnquiryStats();
  const { data: flagData, isLoading: flagsLoading, isError: flagsError } = useEnquiryFlags(permissions.can('enquiries', 'read'));

  // Flags describe ACTIVE enquiries, so on the Archived tab and the stages that archive on entry
  // (Enrolled, Spam) no row can carry one and the filter would always empty the list (review L2).
  // There the control is disabled and the flag in the URL is not applied (it resumes on a tab where
  // it can match).
  const flagsApply = tab !== 'ARCHIVED' && !(tab !== 'ACTIVE' && ARCHIVING_STAGES.includes(tab));
  const flag: EnquiryFlagKey | undefined = flagsApply ? urlFlag : undefined;

  const filters: EnquiryFilters = {
    ...(tab === 'ARCHIVED'
      ? { archived: 'true' as const }
      : tab === 'ACTIVE'
        ? {}
        : ARCHIVING_STAGES.includes(tab)
          ? { archived: 'true' as const, stage: tab }
          : { stage: tab }),
    ...(owner !== 'ALL' ? { owner } : {}),
    ...(needsReviewOnly ? { needsReview: 'true' as const } : {}),
    ...(flag ? { flag } : {}),
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    ...SORTS[sort],
    page: page + 1,
    limit: rowsPerPage,
  };
  const { data, isLoading, isError, error, refetch } = useEnquiries(filters);

  const archiveMutation = useArchiveEnquiry();
  const deleteMutation = useDeleteEnquiry();
  // "Take it": the owner only; the next action and its due day stay as they are.
  const takeIt = useSetNextAction();
  const temperatureEnabled = config?.settings.temperatureEnabled === true;
  const columns = temperatureEnabled ? 11 : 10;

  const enquiries = data?.data ?? [];
  const totalCount = data?.total ?? 0;

  const stageCount = (stage: EnquiryStage): number =>
    (ARCHIVING_STAGES.includes(stage) ? stats?.archivedByStage?.[stage] : stats?.byStage?.[stage]) ?? 0;

  const stageTabs = useMemo(
    () =>
      ENQUIRY_STAGES.filter((stage) => {
        if (stage === 'RESOLVED_TO_REVIEW') return false;
        return stageView(config, stage)?.enabled === true || stageCount(stage) > 0;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config, stats],
  );

  // A `?tab=` naming a stage the school does not show (disabled, no rows) has no Tab, and MUI warns
  // about an out-of-range value: once config and counts are in, fall back to New (F-N1).
  useEffect(() => {
    if (!config || !stats) return;
    if (tab === 'ACTIVE' || tab === 'ARCHIVED' || stageTabs.includes(tab)) return;
    setTab('NEW');
    setPage(0);
  }, [config, stats, tab, stageTabs]);

  const filtered = !!debouncedSearch || owner !== 'ALL' || needsReviewOnly || !!flag || tab !== 'ACTIVE';

  const stageOnlyTab = tab !== 'ACTIVE' && tab !== 'ARCHIVED';
  const narrowed = !!debouncedSearch || owner !== 'ALL' || needsReviewOnly || !!flag;
  const emptyText =
    stageOnlyTab && !narrowed
      ? `Nothing at ${stageLabel(config, tab)} right now`
      : filtered
        ? 'No enquiries match your filters'
        : 'No enquiries yet';

  const resetPage = () => setPage(0);

  const changeFlag = (next: EnquiryFlagKey | '') => {
    const params = new URLSearchParams(searchParams);
    if (next) params.set('flag', next);
    else params.delete('flag');
    // `?tab=` is read once into state, so a stale one (the dashboard's `?tab=NEW`) would reopen a
    // different tab on refresh: write the tab on screen back (review L2).
    if (tab === 'ACTIVE') params.delete('tab');
    else params.set('tab', tab);
    setSearchParams(params, { replace: true });
    resetPage();
  };

  // Only flags that are switched on (a non-null count) are offered, each with its count; the one in
  // the URL stays selectable even when it is not offered, so the control never shows a blank value.
  const flagCounts = flagData?.counts;
  const offeredFlags = ENQUIRY_FLAG_KEY_LIST.filter((k) => flagCounts?.[k] != null || k === urlFlag);

  const handleRowClick = (enquiry: EnquiryListItem) => {
    setSelectedEnquiryId(enquiry.id);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedEnquiryId(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTargetId) return;
    try {
      await deleteMutation.mutateAsync(deleteTargetId);
      setDeleteTargetId(null);
    } catch {
      // the hook reports the failure; the confirm stays open to try again
    }
  };

  const summary: Array<{ label: string; value: number | undefined }> = [
    { label: 'Active', value: stats?.active },
    { label: 'Unassigned', value: stats?.unassigned },
    { label: 'Needs review', value: stats?.needsReview },
    { label: 'Archived', value: stats?.archived },
  ];

  return (
    <Box>
      <PageHeader
        title="Enquiries"
        subtitle="Follow each family from first enquiry to enrolment"
        {...(mayWrite
          ? { actionLabel: 'Add Enquiry', actionIcon: <AddIcon />, onAction: () => setCreateDialogOpen(true) }
          : {})}
      />

      {/* Summary */}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        {summary.map((item) => (
          <Paper key={item.label} sx={{ px: 2, py: 1, minWidth: 110 }}>
            <Typography variant="h6" fontWeight="bold" component="div">
              {item.value === undefined ? <Skeleton width={32} /> : item.value}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {item.label}
            </Typography>
          </Paper>
        ))}
      </Stack>

      {mayWrite && (stats?.toReview ?? 0) > 0 && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => setReviewOpen(true)}>
              Review
            </Button>
          }
        >
          To review ({stats?.toReview}): old Resolved enquiries need an outcome.
        </Alert>
      )}

      {/* Tabs and filters */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, value: TabValue) => {
            setTab(value);
            resetPage();
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab value="ACTIVE" label={stats ? `Active (${stats.active})` : 'Active'} />
          {stageTabs.map((stage) => (
            <Tab
              key={stage}
              value={stage}
              label={`${stageLabel(config, stage)}${stats ? ` (${stageCount(stage)})` : ''}`}
            />
          ))}
          <Tab value="ARCHIVED" label="Archived" />
        </Tabs>

        <Box sx={{ p: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            <TextField
              size="small"
              placeholder="Search name, email, phone, subject, message or child..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: { xs: 0, sm: 260 }, flexGrow: { xs: 1, sm: 0 } }}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="enquiry-owner-label">Owner</InputLabel>
              <Select
                labelId="enquiry-owner-label"
                label="Owner"
                value={owner}
                onChange={(e) => {
                  setOwner(e.target.value as OwnerFilter);
                  resetPage();
                }}
              >
                <MenuItem value="ALL">All</MenuItem>
                <MenuItem value="me">Mine</MenuItem>
                <MenuItem value="unassigned">Unassigned</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="enquiry-sort-label">Sort</InputLabel>
              <Select
                labelId="enquiry-sort-label"
                label="Sort"
                value={sort}
                onChange={(e) => {
                  setSort(e.target.value as SortChoice);
                  resetPage();
                }}
              >
                <MenuItem value="due">Due first</MenuItem>
                <MenuItem value="received">Received newest</MenuItem>
                <MenuItem value="activity">Last activity</MenuItem>
              </Select>
            </FormControl>
            {(flagsLoading || flagsError || offeredFlags.length > 0 || !!urlFlag) && (
              <FormControl size="small" sx={{ minWidth: 160 }} disabled={flagsLoading || flagsError || !flagsApply}>
                <InputLabel id="enquiry-flag-label">Flag</InputLabel>
                <Select
                  labelId="enquiry-flag-label"
                  label="Flag"
                  value={flag ?? ''}
                  onChange={(e) => changeFlag(e.target.value as EnquiryFlagKey | '')}
                >
                  <MenuItem value="">All</MenuItem>
                  {offeredFlags.map((k) => (
                    <MenuItem key={k} value={k}>
                      {ENQUIRY_FLAG_LABELS[k]}
                      {flagCounts?.[k] != null ? ` (${flagCounts[k]})` : ''}
                    </MenuItem>
                  ))}
                </Select>
                {flagsError ? (
                  <FormHelperText>Could not load flags</FormHelperText>
                ) : !flagsApply ? (
                  <FormHelperText>Flags apply to active enquiries</FormHelperText>
                ) : null}
              </FormControl>
            )}
            <ToggleButton
              value="needsReview"
              size="small"
              selected={needsReviewOnly}
              onChange={() => {
                setNeedsReviewOnly((v) => !v);
                resetPage();
              }}
            >
              Needs review{stats ? ` (${stats.needsReview})` : ''}
            </ToggleButton>
            <Tooltip title="Refresh">
              <IconButton onClick={() => refetch()} size="small" aria-label="Refresh">
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
      </Paper>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {apiErrorMessage(error, 'Failed to load enquiries. Please try again.')}
        </Alert>
      )}

      <Paper>
        <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
          <Table>
            {/* Headers and short values never wrap: the table scrolls sideways instead. */}
            <TableHead sx={{ '& th': { whiteSpace: 'nowrap' } }}>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Interest</TableCell>
                <TableCell>Stage</TableCell>
                {temperatureEnabled && <TableCell>Temperature</TableCell>}
                <TableCell>Ball with</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Next action</TableCell>
                <TableCell>Last activity</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>M&amp;G date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(columns)].map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : isError ? null : enquiries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns} align="center" sx={{ py: 6 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <QuestionAnswerIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                      <Typography color="text.secondary">
                        {emptyText}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                enquiries.map((enquiry) => {
                  const colors = STAGE_COLORS[enquiry.stage];
                  const due = dueState(enquiry.nextActionDueDate, config?.schoolToday);
                  const owner = ownerLabel(enquiry, ownerTitle, ownerChipColors);
                  const interests = interestChips(enquiry);
                  return (
                    <TableRow
                      key={enquiry.id}
                      hover
                      onClick={() => handleRowClick(enquiry)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                          <Typography variant="body2" fontWeight={enquiry.stage === 'NEW' ? 600 : 400} sx={{ whiteSpace: 'nowrap' }}>
                            {enquiry.firstName} {enquiry.lastName}
                          </Typography>
                          <SpamCheckChip spamCheck={enquiry.spamCheck} tooltip={ENQUIRY_SPAM_CHECK_TOOLTIP} />
                          {enquiry.migrationReviewDueAt && <Chip size="small" variant="outlined" label="Needs review" />}
                          <EnquiryFlagChips flags={enquiry.flags} />
                          {enquiry.possibleDuplicate && (
                            <Tooltip title="Another enquiry has the same email or phone">
                              <Chip
                                size="small"
                                label="Possible duplicate"
                                // Dark text on the warning tint, never outlined coral on white (#563; review L8).
                                sx={{ bgcolor: statusColors.warningBg, color: brandColors.textPrimary }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {interests.length > 0 ? (
                          // One chip per distinct interest, a matched instrument in its own colour (as on the Teachers page).
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                            {interests.map((i) => (
                              <Chip
                                key={i.key}
                                size="small"
                                label={i.label}
                                variant={i.color ? 'filled' : 'outlined'}
                                sx={i.color ? { bgcolor: i.color, color: getContrastTextColor(i.color) } : undefined}
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={stageLabel(config, enquiry.stage)}
                          sx={{ bgcolor: colors.bg, color: colors.text, fontWeight: 600 }}
                        />
                      </TableCell>
                      {temperatureEnabled && (
                        <TableCell>
                          {enquiry.temperature ? (
                            <Chip
                              size="small"
                              label={TEMPERATURE_LABELS[enquiry.temperature]}
                              sx={{ bgcolor: TEMPERATURE_COLORS[enquiry.temperature].bg, color: TEMPERATURE_COLORS[enquiry.temperature].text }}
                            />
                          ) : (
                            <Typography variant="body2" color="text.disabled">—</Typography>
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                          {carriesBall(enquiry.stage) ? ballWithLabel(config, enquiry.ballWith) : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {/* The person (or "Unassigned") on one line, their role or group as a chip under it,
                            filled in the role's colour (#629). "Unassigned" is also marked in the overdue
                            date's colour, because it needs someone to pick it up. */}
                        <Typography
                          variant="body2"
                          color={owner.unassigned ? 'error.dark' : 'text.primary'}
                          fontWeight={owner.unassigned ? 600 : 400}
                          sx={{ whiteSpace: 'nowrap' }}
                          data-testid={owner.unassigned ? 'owner-unassigned' : undefined}
                        >
                          {owner.name}
                        </Typography>
                        {owner.role && (
                          <Chip
                            size="small"
                            // Filled in the role's colour, as every role chip is (the Roles page, the user menu, #629);
                            // text by contrast. A role with no colour (a support account, an uncoloured custom role) stays outlined.
                            variant={owner.roleColor ? 'filled' : 'outlined'}
                            label={owner.role}
                            sx={{
                              mt: 0.5,
                              ...(owner.roleColor ? { bgcolor: owner.roleColor, color: getContrastTextColor(owner.roleColor) } : {}),
                              ...(owner.unassigned && !owner.roleColor ? { borderColor: brandColors.coral } : {}),
                            }}
                          />
                        )}
                        {owner.unassigned && mayWrite && user?.id && (
                          <Button
                            size="small"
                            sx={{ px: 0, minWidth: 0, display: 'block' }}
                            disabled={takeIt.isPending}
                            onClick={(e) => {
                              e.stopPropagation();
                              takeIt.mutate({ id: enquiry.id, data: { ownerUserId: user.id } });
                            }}
                          >
                            Take it
                          </Button>
                        )}
                      </TableCell>
                      <TableCell>
                        {enquiry.nextAction || enquiry.nextActionDueDate ? (
                          <>
                            {/* One line; a long action is cut with "…" and shown whole on hover. */}
                            <Tooltip title={enquiry.nextAction ?? ''} disableHoverListener={!enquiry.nextAction}>
                              <Typography
                                variant="body2"
                                sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 280 }}
                              >
                                {enquiry.nextAction}
                              </Typography>
                            </Tooltip>
                            {enquiry.nextActionDueDate && (
                              <Typography
                                variant="caption"
                                display="block"
                                sx={{ whiteSpace: 'nowrap' }}
                                color={due === 'OVERDUE' ? 'error.dark' : 'text.secondary'}
                                fontWeight={due === 'OVERDUE' || due === 'TODAY' ? 600 : 400}
                              >
                                {due === 'TODAY' ? 'Today' : formatDay(enquiry.nextActionDueDate)}
                                {due === 'OVERDUE' ? ' (overdue)' : ''}
                              </Typography>
                            )}
                          </>
                        ) : (
                          <Typography variant="body2" color="text.disabled">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                          {dayIn(enquiry.lastActivityAt, timeZone)}
                        </Typography>
                        <Typography variant="caption" color="text.disabled" display="block" sx={{ whiteSpace: 'nowrap' }}>
                          Received {formatDay(enquiry.receivedOn)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                          {enquiry.SourceOption?.label ?? 'Not asked'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                          {enquiry.MeetAndGreet?.scheduledDateTime ? dayIn(enquiry.MeetAndGreet.scheduledDateTime, timeZone) : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {(mayWrite || mayDeleteEnquiry) && (
                          <IconButton
                            size="small"
                            aria-label="Row actions"
                            onClick={(e) => {
                              e.stopPropagation();
                              setMenu({ anchor: e.currentTarget, enquiry });
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Paper>

      <Menu anchorEl={menu?.anchor ?? null} open={!!menu} onClose={() => setMenu(null)}>
        {mayWrite && menu && mayToggleArchive(menu.enquiry) && (
          <MenuItem
            onClick={() => {
              archiveMutation.mutate({ id: menu.enquiry.id, archived: !isArchived(menu.enquiry.archivedAt) });
              setMenu(null);
            }}
          >
            {isArchived(menu.enquiry.archivedAt) ? 'Unarchive' : 'Archive'}
          </MenuItem>
        )}
        {mayDeleteEnquiry && menu && (
          <MenuItem
            sx={{ color: 'error.main' }}
            onClick={() => {
              setDeleteTargetId(menu.enquiry.id);
              setMenu(null);
            }}
          >
            Delete
          </MenuItem>
        )}
      </Menu>

      <EnquiryDetailDrawer enquiryId={selectedEnquiryId} open={drawerOpen} onClose={handleDrawerClose} />

      {mayWrite && <CreateEnquiryDialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} />}

      {mayWrite && <MigrationReviewDialog open={reviewOpen} onClose={() => setReviewOpen(false)} />}

      <ConfirmDialog
        open={!!deleteTargetId}
        title="Delete Enquiry"
        message="Are you sure you want to permanently delete this enquiry? This action cannot be undone."
        confirmLabel="Delete"
        confirmColor="error"
        loading={deleteMutation.isPending}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTargetId(null)}
      />
    </Box>
  );
}
